// list-groups.component.ts
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NzTreeNodeOptions } from 'ng-zorro-antd/core/tree';
import { lastValueFrom } from 'rxjs';
import { AuthService } from 'src/app/core/services/AuthService';
import { GruposService, Grupo as GrupoApi } from 'src/app/core/services/grupos.service'; // 👈 usa el servicio
import { GroupsComponent } from 'src/app/shared/components/groups/groups.component';
import { PermisosModalComponent } from 'src/app/shared/components/permisos-acceso/permisos-modal.component';
import Swal, { SweetAlertIcon } from 'sweetalert2';

// Lo que espera el componente internamente
interface Grupo {
  id: number;
  nombre: string;
  padre_id: number | null;
  orden: number;
  tipoGrupo: string;
  publica: boolean;
}

@Component({
  selector: 'app-list-groups',
  templateUrl: './list-groups.component.html',
  styleUrls: ['./list-groups.component.scss']
})
export class ListGroupsComponent implements OnInit {

  grupos: Grupo[] = [];
  byId = new Map<number, Grupo>();
  idxHijos = new Map<number | null, number[]>();

  treeNodes: NzTreeNodeOptions[] = [];
  seleccion: number | null = null;
  filtrados: Grupo[] = [];
  @ViewChild(GroupsComponent) groups!: GroupsComponent;


  formGrupo: FormGroup;
  groupSelected: any = null;
  esEdicion: boolean = false;
  accesUser: boolean = false;
  // Toast
  toast = Swal;


  constructor(private gruposService: GruposService,
    private modalService: NgbModal, // ¡Inyecta el servicio NgbModal!
    private router: Router,
    private authService: AuthService,
    private fb: FormBuilder
  ) {

    this.formGrupo = this.fb.group({
      nombre: new FormControl('', [Validators.required]),
      padreId: new FormControl(null, []),
      tipoGrupo: new FormControl('', []),
      publica: [false]
    });
    this.initReactions();

  }

  ngOnInit(): void {
    this.accesUser = this.authService.hasRoleAuth('ADMINISTRADOR');
    // si quieres solo raíces usa this.load(true)
    this.toastAlert();
    this.load(false); // false = todos; true = solo raíces
  }

  initReactions() {
    this.formGrupo.get('padreId')?.valueChanges.subscribe(value => {
      const tipoGrupoCtrl = this.formGrupo.get('tipoGrupo');

      if (value === null) {
        // Mostrar selector + hacerlo requerido
        tipoGrupoCtrl?.setValidators([Validators.required]);
      } else {
        // Ocultarlo + quitar validación
        tipoGrupoCtrl?.clearValidators();
        tipoGrupoCtrl?.setValue(null);
      }

      tipoGrupoCtrl?.updateValueAndValidity();
    });
  }


  private load(onlyRoots: boolean) {
    const src$ = onlyRoots
      ? this.gruposService.getRoots() // GET /grupos?roots=true
      : this.gruposService.getAll();  // GET /grupos

    src$.subscribe({
      next: (data: GrupoApi[]) => {
        this.grupos = this.normalizar(data);
        this.rebuildIndices();
        this.refreshTree();
        this.aplicarFiltro();
      },
      error: (err) => {

      }
    });
  }

  /** Convierte API { padre } → interno { padre_id } */
  private normalizar(data: GrupoApi[]): Grupo[] {
    return (data ?? []).map(r => ({
      id: r.id,
      nombre: r.nombre,
      padre_id: r.padre ? r.padre.id : null,
      orden: r.orden ?? 0,
      tipoGrupo: r.tipoGrupo,
      publica: r.publica,
    }));
  }

  rebuildIndices(): void {
    this.byId.clear();
    this.idxHijos.clear();

    for (const g of this.grupos) {
      this.byId.set(g.id, g);
      const k = g.padre_id ?? null;
      if (!this.idxHijos.has(k)) this.idxHijos.set(k, []);
      this.idxHijos.get(k)!.push(g.id);
    }

    // ordena hijos por 'orden'
    for (const [k, lista] of this.idxHijos.entries()) {
      lista.sort((a, b) => (this.byId.get(a)!.orden ?? 0) - (this.byId.get(b)!.orden ?? 0));
      this.idxHijos.set(k, lista);
    }
  }


  modalCrearGrupo(content, groupSelected: any) {
    this.esEdicion = false;
    this.groupSelected = groupSelected;
    this.formGrupo.reset();
    this.formGrupo.markAsPristine();
    this.formGrupo.markAsUntouched();
    this.formGrupo.updateValueAndValidity();

    this.formGrupo.patchValue({
      padreId: this.groupSelected?.id ? this.groupSelected?.id : null,
    });

    this.formGrupo.get('padreId')?.updateValueAndValidity({ emitEvent: true });

    this.openModal(content);
  }

  modalEditarGrupo(content, groupSelected: any) {
    this.esEdicion = true;
    this.groupSelected = groupSelected;
    this.openModal(content);

    this.formGrupo.patchValue({
      nombre: this.groupSelected.nombre,
      padreId: this.groupSelected.padre_id,
      tipoGrupo: this.groupSelected.tipoGrupo,
      publica: this.groupSelected.publica
    });

    this.formGrupo.get('padreId')?.updateValueAndValidity({ emitEvent: true });
  }

  async generarAccion() {
    if (this.esEdicion) {
      this.editarGrupo();
    } else {
      this.guardarGrupo();
    }
  }

  async editarGrupo() {
    if (this.formGrupo.valid) {

      if (this.formGrupo.value.padreId !== null) {
        this.formGrupo.patchValue({
          tipoGrupo: this.groupSelected?.tipoGrupo
        });
      }

      await lastValueFrom(this.gruposService.update(this.groupSelected.id, { ...this.formGrupo.value }))

      Swal.fire({
        title: '¡Grupo editado!',
        text: 'El grupo fue editado correctamente.',
        icon: 'success',
        confirmButtonText: 'Ok'
      });
      this.groups.load(false);
      this.load(false); // false = todos; true = solo raíces
      this.modalService.dismissAll();
    } else {
      Swal.fire({
        title: '¡Campos requeridos!',
        text: 'El campo nombre es requerido.',
        icon: 'warning',
        confirmButtonText: 'Ok'
      });
    }
  }

  async guardarGrupo() {

    if (!this.formGrupo.valid) {
      Swal.fire({
        title: '¡Campos requeridos!',
        text: 'El campo nombre es requerido.',
        icon: 'warning',
        confirmButtonText: 'Ok'
      });
      return;
    }

    if (this.formGrupo.value.padreId !== null) {
      this.formGrupo.patchValue({
        tipoGrupo: this.groupSelected?.tipoGrupo
      });
    }

    try {

      await lastValueFrom(
        this.gruposService.create({ ...this.formGrupo.value })
      );

      Swal.fire({
        title: '¡Grupo agregado!',
        text: 'El grupo fue agregado correctamente.',
        icon: 'success',
        confirmButtonText: 'Ok'
      });

      this.groups.load(false);
      this.load(false);
      this.modalService.dismissAll();

    } catch {
      // No haces nada.
      // El interceptor ya mostró el mensaje correspondiente.
    }
  }

  refreshTree(): void {
    const map = new Map<number, NzTreeNodeOptions & { _orden?: number }>();
    const roots: (NzTreeNodeOptions & { _orden?: number })[] = [];

    for (const g of this.grupos) {
      map.set(g.id, {
        title: g.nombre,
        key: String(g.id),
        value: g.id,
        selectable: true,
        children: [],
        _orden: g.orden
      });
    }

    for (const g of this.grupos) {
      const node = map.get(g.id)!;
      if (g.padre_id == null) {
        roots.push(node);
      } else {
        const p = map.get(g.padre_id);
        if (p) {
          (p.children ||= []).push(node);
        } else {
          roots.push(node);
        }
      }
    }

    const sortRec = (nodes: (NzTreeNodeOptions & { _orden?: number })[]) => {
      nodes.sort((a, b) => (a._orden ?? 0) - (b._orden ?? 0));
      for (const n of nodes) {
        if (n.children && n.children.length) sortRec(n.children as any);
      }
    };
    sortRec(roots);

    this.treeNodes = roots;
  }

  private collectDescendants(rootId: number): Set<number> {
    const keep = new Set<number>();
    const q = [rootId];
    while (q.length) {
      const cur = q.shift()!;
      if (keep.has(cur)) continue;
      keep.add(cur);
      for (const h of this.idxHijos.get(cur) ?? []) q.push(h);
    }
    return keep;
  }

  onPick(val: number | string | null): void {
    this.seleccion = (val === null || val === undefined || val === '') ? null : Number(val);
    this.aplicarFiltro();
  }

  aplicarFiltro(): void {
    if (this.seleccion == null) {
      this.filtrados = [...this.grupos];
      return;
    }
    const keep = this.collectDescendants(this.seleccion);
    this.filtrados = this.grupos.filter(g => keep.has(g.id));
  }

  padreNombre(g: Grupo): string {
    if (g.padre_id == null) return '—';
    return this.byId.get(g.padre_id)?.nombre ?? '—';
  }


  openModal(content: any) {
    this.modalService.open(content, { centered: true, size: 'lg' });
  }

  redericGrupoUser(id: number) {
    this.router.navigate(['groups/group-user', id]);
  }

  eliminarGrupo(id: number) {
    Swal.fire({
      title: "¡Eliminación de grupo/subgrupo!",
      text: "Deseas eliminar este grupo.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Eliminar",
      cancelButtonText: "Cancelar",
    }).then(async (result) => {
      if (result.isConfirmed) {

        await lastValueFrom(this.gruposService.remove(id));
        this.groups.load(false);
        this.load(false);
        this.alert('success', 'Grupo eliminado correctamente');
      }
    });
  }

  canManageItem(item: any) {
    return this.authService.canManageItem(item, this.accesUser)
  }


  toastAlert() {
    this.toast = Swal.mixin({
      toast: true,
      position: "top-end",
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
      didOpen: (toast) => {
        toast.onmouseenter = Swal.stopTimer;
        toast.onmouseleave = Swal.resumeTimer;
      }
    });
  }

  alert(icontext: SweetAlertIcon, title: string, text: string = '') {
    this.toast.fire({
      icon: icontext,
      title: title,
      text: text
    });
  }

  async gestionarAcceso(id: any) {
    const modalRef = this.modalService.open(PermisosModalComponent, {
      centered: true,
      size: 'xl',
    });
    modalRef.componentInstance.id = id;
    modalRef.componentInstance.modeloAcceso = 'Grupo';
  }

}
